import React, { useMemo } from 'react'
import ReviewsCard from './ReviewsCard'
import type {
  FilterableTaxon,
  PageType,
  SponsorTier,
  HighlightReview,
  CenterFeed
} from '@/types/models'
import type { PrimaryFocus } from '@/domain/primaryFocus/constant'
import { isArrayWithLessThanNValues } from '@/utils/checks'
import { generateReviewSectionTitle } from '@/utils/generateReviewSectionTitle'
import SegmentCardTracker from '../CenterCards/SegmentCardTracker'
import { filterReviewsByTermOrPrimaryFocus } from '@/domain/centerReviews/filterReviewsByTermOrPrimaryFocus'
import { getUniqueReviews } from '@/domain/centerReviews/getUniqueReviews'
import { SPOTLIGHT_BLOCK_REVIEW_COUNT } from '@/domain/centerReviews/constants'
import { isLocationPageType } from '@/domain/useCases/pageTypeCheck'
import { filterReviewsByGlobalTerm } from '@/domain/centerReviews/filterReviewsByGlobalTerm'
import HorizontalScroll from '@/components/atoms/HorizontalScroll/HorizontalScroll'
const shouldHideReviews = isArrayWithLessThanNValues(
  SPOTLIGHT_BLOCK_REVIEW_COUNT
)

const HighlightedReviewsBlock = ({
  title,
  reviews,
  pageType,
  filterableTaxonomies,
  primaryFocus,
  isGlobalTerm
}: {
  title: string
  reviews: HighlightReview[]
  pageType: PageType
  filterableTaxonomies: FilterableTaxon[]
  primaryFocus: PrimaryFocus
  isGlobalTerm?: boolean
}): JSX.Element | null => {
  const reviewsWithUniqueCenters = useMemo(() => {
    const filteredReviews: HighlightReview[] =
      filterReviewsByTermOrPrimaryFocus({
        reviews,
        filterableTaxonomies,
        pageType,
        primaryFocus,
        title
      }) as HighlightReview[]

    if (isLocationPageType(pageType)) {
      return getUniqueReviews(filteredReviews)
    }
    const filteredTermReviewsByGlobal = filterReviewsByGlobalTerm({
      reviews: filteredReviews,
      isGlobalTerm: isGlobalTerm ?? false
    })

    return getUniqueReviews(filteredTermReviewsByGlobal)
  }, [reviews])

  const headerTitle = generateReviewSectionTitle({
    pageType,
    title,
    primaryFocus
  })

  if (shouldHideReviews(reviewsWithUniqueCenters)) {
    return null
  }

  return (
    <div className="-mx-4 flex flex-col gap-4 border border-blue-50 bg-blue-25 p-4 md:mx-0 md:rounded-[20px] md:p-6">
      <div className="flex items-center justify-between">
        <h2 className="font-FieldsVariable text-lg font-medium leading-6 text-blue-950">
          {headerTitle}
        </h2>
      </div>

      <div>
        <HorizontalScroll
          id={`highlighted-reviews`}
          alwaysScroll
          count={3}
          minWidth={'400px'}
          maxWidth="30vw"
          gridClassName="h-[323px] max-h-[323px] lg:h-[343px] lg:max-h-[343px]"
          marginBottom="0px"
          withGradient={false}
          scrollOverPadding
          removeArrowBorderOffset
        >
          {reviewsWithUniqueCenters.map((review: HighlightReview, i) => {
            const center = review._center
            return (
              <SegmentCardTracker
                key={center?.slug}
                center={
                  {
                    title: center.title as string,
                    slug: center.slug as string,
                    sponsor_tier: center.sponsor_tier as SponsorTier,
                    position: i + 1
                  } as CenterFeed
                }
                position={i + 1}
                zone="Spotlight"
              >
                <ReviewsCard
                  slug={center.slug as string}
                  rating={review.overall_star}
                  capacity={review.reviewer_type as string}
                  reviewImage={
                    review._center?.featured_image?.replace(
                      'upload/',
                      'upload/e_gen_restore/c_fill,w_48,h_48,q_15/f_auto/'
                    ) || ''
                  }
                  title={review._center.title as string}
                  name={review.name || 'Anonymous'}
                  shortDescription={review.review_body}
                />
              </SegmentCardTracker>
            )
          })}
        </HorizontalScroll>
      </div>
    </div>
  )
}

export default HighlightedReviewsBlock
